import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Store, Select, Actions, ofActionDispatched } from '@ngxs/store';
import { NextView, PreviousView } from '../../state/edit.actions';
import { Observable, Subject } from 'rxjs';
import { EngineState } from 'src/app/engine/state/engine.state';
import { filter, takeUntil } from 'rxjs/operators';
import { EditPaintState } from '../../state/paint/edit.paint.state';
import { ViewTreeNode } from '../../../engine/models/view.tree-node';

import { ClickNode, SetActiveTool } from 'src/app/engine/state/engine.actions';
import { ColorRecipeModel } from '../../models/color-recipe.model';
import { PaintService } from 'src/app/engine/paint.service';
import { ToolEnum } from 'src/app/engine/models/tool.enum';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { RecordChapterPaintState } from 'src/app/story/state/chapters/story.chapters.actions';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import { EditStateModel } from '../../state/edit.state.model';
import { EditState } from '../../state/edit.state';
import { PaintPresetOriginal } from 'src/app/engine/models/paint-presets.const';


@Component({
  selector: 'app-edit-paint-context',
  templateUrl: './edit-paint-context.component.html',
  styleUrls: ['./edit-paint-context.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditPaintContextComponent implements OnInit, OnDestroy {
  activeToolEnum = ToolEnum;
  
  @Select(EngineState.getActiveTool)
  activeTool$: Observable<ToolEnum>;

  @Select(EngineNodesState.getCheckedNodes)
  checkedNodes$: Observable<ViewTreeNode>;

  @Select(EditPaintState.getColorRecipe)
  colorRecipe$: Observable<ColorRecipeModel>;

  @Select(StoryChaptersState.getSelectedChapterId)
  selectedChapterId$: Observable<string>;

  @Select(StoryChaptersState.getSelectedChapter)
  selectedChapter$: Observable<ChapterModel>;

  interactive: boolean;

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private actions: Actions,
    private paintService: PaintService,
    private changeDetector: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.bindApplicationEvents();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  bindApplicationEvents() {
    // Listen for clicked node, ensure we're in paint mode
    this.actions
      .pipe(
        ofActionDispatched(ClickNode),
        filter((nodeAction: ClickNode) => {
          return nodeAction.node != null &&
            this.store.selectSnapshot(EngineState.getActiveTool) === ToolEnum.Paint &&
            !nodeAction.event.srcEvent.shiftKey;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((clickNode: ClickNode) => {
        // record and set interactive pick
        const colorRecipe = this.store.selectSnapshot(EditPaintState.getColorRecipe);
        this.paintNodes([clickNode.node], colorRecipe);
      });

    // Only show when model is loaded
    this.selectedChapter$
      .pipe(
        filter((chapter) => chapter != null),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((chapter) => {
        this.interactive = chapter.readyState.model;
        this.changeDetector.markForCheck();
      });
  }
  
  handleClickTool(tool: ToolEnum) {
    this.store.dispatch(new SetActiveTool(tool));
  }

  handleClearChapterPaint() {
    const nodes = Object.values(this.store.selectSnapshot(EngineState.getViewGraph).nodes);
    const recipe = PaintPresetOriginal.recipe;
    this.paintNodes(nodes, recipe);
  }
  
  handlePaintNodesClick() {
    const checked = this.store.selectSnapshot(EngineNodesState.getCheckedNodes); 
    const recipe = this.store.selectSnapshot(EditPaintState.getColorRecipe);
    this.paintNodes(checked, recipe);
  }

  // paint and conditionally record
  paintNodes(nodes: ViewTreeNode[], recipe: ColorRecipeModel) {
    const record = this.store.selectSnapshot(EditState.getRecord);

    // relay paint to engine
    this.paintService.paintAll(nodes, recipe);

    // record
    this.store.dispatch(new RecordChapterPaintState(nodes, recipe, record));
  }

  handleNext() {
    this.store.dispatch(new NextView());
  }

  handlePrevious() {
    this.store.dispatch(new PreviousView());
  }
}
